import React, { useEffect, useRef } from 'react';

export const MT5CredentialsModal = ({ isOpen, onClose, form, setForm, onSave, saving }) => {
  // Reference to the first input for accessibility
  const loginInputRef = useRef(null);

  // Focus the login field when modal opens
  useEffect(() => {
    if (isOpen && loginInputRef.current) {
      loginInputRef.current.focus();
    }
  }, [isOpen]);

  // Handle form submission via Enter key
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!saving && form.login && form.password && form.server) {
      onSave();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/40 dark:bg-black/70 flex justify-center items-center z-50 p-4 backdrop-blur-sm"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 w-full max-w-md shadow-2xl transform transition-all">
        
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h3 id="modal-title" className="text-lg font-semibold text-gray-900 dark:text-white">
            Connect MT5 Account
          </h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close modal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* FIX: Wrapped in a <form> for proper semantics and password manager integration */}
        <form onSubmit={handleSubmit} className={`space-y-4 transition-opacity duration-200 ${saving ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
          
          {/* Login */}
          <div>
            <label htmlFor="mt5-login" className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">Account Login</label>
            <input
              id="mt5-login"
              ref={loginInputRef}
              type="text" 
              placeholder="e.g. 8123456" 
              value={form.login}
              onChange={(e) => setForm({ ...form, login: e.target.value })}
              // FIX: Autocomplete off for login to prevent leaking other site usernames
              autoComplete="off"
              className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="mt5-password" className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">Password</label>
            <input
              id="mt5-password"
              type="password" 
              placeholder="••••••••" 
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              // FIX: VULNERABILITY PATCHED
              // autoComplete="new-password" prevents the browser from auto-filling 
              // passwords from other sites or previously saved sessions. 
              // It also signals the browser to handle this credential securely.
              autoComplete="new-password"
              className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
            />
          </div>

          {/* Server */}
          <div>
            <label htmlFor="mt5-server" className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wider">Server</label>
            <input
              id="mt5-server"
              type="text" 
              placeholder="e.g. MetaQuotes-Demo" 
              value={form.server}
              onChange={(e) => setForm({ ...form, server: e.target.value })}
              autoComplete="off"
              className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
            />
          </div>

          {/* Account Type Toggle */}
          <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/30 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <span className="text-sm text-gray-700 dark:text-gray-300">Is this a Demo Account?</span>
            <button
              type="button"
              onClick={() => setForm({ ...form, is_demo: !form.is_demo })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-gray-900 ${form.is_demo ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
              aria-pressed={form.is_demo}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${form.is_demo ? 'translate-x-6' : 'translate-x-1'}`}
              />
            </button>
          </div>

          {/* Actions - Moved buttons inside form or kept outside depending on layout preference, but Submit button is inside form now */}
          <div className="flex gap-3 mt-8 pt-4 border-t border-gray-100 dark:border-gray-700">
            <button 
              type="button"
              onClick={onClose} 
              disabled={saving}
              className="flex-1 p-3 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-100 disabled:text-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600 dark:disabled:bg-gray-800 dark:disabled:text-gray-500 rounded-lg text-gray-700 dark:text-gray-100 font-medium transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={saving || !form.login || !form.password || !form.server} 
              className="flex-1 p-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 dark:disabled:bg-blue-900 disabled:text-blue-100 dark:disabled:text-blue-300 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors shadow-lg shadow-blue-500/20 dark:shadow-blue-900/20"
            >
              {saving ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Connecting...
                </span>
              ) : "Save & Connect"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};